/**
 * @coeldery/style-picker — **風格選項**（多選 chips ＋ 自由填寫）
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「for the 換風格, can it pop out options to choose from,
 *   and of course an empty space to manually fill in too」；`decisions/026`）→ 依 R3 做模組。
 *
 * ⚠️ 模組**唔認識**音樂 provider：只交出「揀咗邊幾個（id）＋自由填寫原文」；
 *   `buildStylePrompt()` 係**純函數**（可單測）→ 由父層傳去做 provider prompt。
 */
import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import type { StyleOption } from './prompt.ts';

export { buildStylePrompt, type StyleOption } from './prompt.ts';

export interface StylePickerProps {
  options: StyleOption[];
  selected: string[];
  onToggle: (id: string) => void;
  freeText: string;
  onFreeTextChange: (value: string) => void;
  /** 自由填寫嘅標題（i18n key） */
  freeLabelKey?: string;
  freePlaceholderKey?: string;
  busy?: boolean;
  /**
   * ⭐ **1.1.0：快速清單**（`decisions/034`；產品負責人 2026-09-24：「initially can choose from a list,
   *   with full style, to put them in the list that to choose from quickly」）
   *   · 傳咗 → 只顯示快速嗰批（＋〔全部風格〕展開）；唔傳 → 全部照出（**向後兼容**）
   */
  quickIds?: string[];
  /** ⭐ 1.1.0：把某個風格加入／移出快速清單（唔傳 ＝ 唔出呢粒掣） */
  onToggleQuick?: (id: string) => void;
  /** ⭐ 1.1.0：唔出自由填寫欄（例：出歌揀曲風一定要由目錄揀 —— 目錄以外冇 prompt） */
  hideFree?: boolean;
  quickLabelKey?: string;
  allLabelKey?: string;
  collapseLabelKey?: string;
  quickAddLabelKey?: string;
}

export default function StylePicker({
  options,
  selected,
  onToggle,
  freeText,
  onFreeTextChange,
  freeLabelKey = 'style_free_label',
  freePlaceholderKey = 'style_free_placeholder',
  busy = false,
  quickIds,
  onToggleQuick,
  hideFree = false,
  quickLabelKey = 'style_quick_label',
  allLabelKey = 'style_show_all',
  collapseLabelKey = 'style_collapse',
  quickAddLabelKey = 'style_quick_add',
}: StylePickerProps) {
  const { t } = useTranslation();
  /** ⭐ 1.1.0：〔全部風格〕展開狀態 */
  const [showAll, setShowAll] = useState(false);
  const quick = quickIds ?? [];
  const useQuick = quick.length > 0;
  const isQuick = (id: string) => quick.includes(id);
  const visible = useQuick && !showAll ? options.filter((o) => isQuick(o.id)) : options;

  return (
    <div data-testid="style-picker" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {useQuick ? (
        <p className="footnote" data-testid="style-quick-label" style={{ margin: 0 }}>
          {t(quickLabelKey)}
        </p>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {visible.map((o) => {
          const on = selected.includes(o.id);
          return (
            <span key={o.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <button
                type="button"
                data-testid="style-option"
                aria-pressed={on}
                disabled={busy}
                onClick={() => onToggle(o.id)}
                className={on ? 'lang-btn lang-btn--active' : 'lang-btn'}
              >
                {o.label}
              </button>
              {/* ⭐ 1.1.0：加入／移出快速清單（只喺「全部風格」模式出） */}
              {useQuick && showAll && onToggleQuick ? (
                <button
                  type="button"
                  data-testid="style-quick-toggle"
                  aria-pressed={isQuick(o.id)}
                  disabled={busy}
                  onClick={() => onToggleQuick(o.id)}
                  title={t(quickAddLabelKey)}
                  style={{
                    minHeight: '28px',
                    padding: '0 8px',
                    borderRadius: '14px',
                    border: '1px solid var(--color-divider)',
                    background: 'transparent',
                    color: 'var(--color-text)',
                    fontFamily: 'inherit',
                    fontSize: 'var(--font-size-tiny, 14px)',
                    cursor: 'pointer',
                  }}
                >
                  {isQuick(o.id) ? '★' : '＋'}
                </button>
              ) : null}
            </span>
          );
        })}
      </div>
      {useQuick ? (
        <button
          type="button"
          data-testid="style-show-all"
          disabled={busy}
          onClick={() => setShowAll((v) => !v)}
          className="lang-btn"
          style={{ alignSelf: 'flex-start' }}
        >
          {showAll ? t(collapseLabelKey) : t(allLabelKey, { n: options.length })}
        </button>
      ) : null}
      {hideFree ? null : (
        <>
          <label style={{ fontSize: 'var(--font-size-small, 15px)', fontWeight: 700 }} htmlFor="style-free">
            {t(freeLabelKey)}
          </label>
          <input
            id="style-free"
            data-testid="style-free"
            value={freeText}
            disabled={busy}
            placeholder={t(freePlaceholderKey)}
            onChange={(e) => onFreeTextChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1.5px solid var(--color-divider)',
              borderRadius: '14px',
              background: 'var(--color-card)',
              color: 'var(--color-text)',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-small, 15px)',
            }}
          />
        </>
      )}
    </div>
  );
}

